import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { ErrorList, FileUploader, InputLabelWrapper } from '@guildeducationinc/recess';
import FileUploadOutlinedIcon from '@guildeducationinc/recess/icons/FileUploadOutlinedIcon';
import { compact, uniqueId, values } from 'lodash';
import * as pdfjsLib from 'pdfjs-dist';
import { useMemo, useRef, useState } from 'react';
import { useLocale } from '../../hooks';
import { useGetS3Url } from '../../hooks/useGetS3Url';
import { useUpload } from '../../hooks/useUpload';
import { LoaderCircleIcon } from '../LoaderCircleIcon';
import { TrackableButton } from '../TrackableButton/TrackableButton';
import styles from './FileUpload.module.scss';
const MIME_TYPES = {
    image: { 'image/jpg': 'jpg', 'image/jpeg': 'jpeg', 'image/png': 'png' },
    document: {
        'application/pdf': 'pdf',
        'image/jpg': 'jpg',
        'image/jpeg': 'jpeg',
        'image/png': 'png',
    }, // pdf, jpg, jpeg, png,
    import: {
        'text/csv': 'csv',
        'application/pgp-encrypted': 'pgp',
        'application/gpg-encrypted': 'gpg',
    },
    export: {
        'text/csv': 'csv',
    },
    json: {
        'application/json': 'json',
    },
};
pdfjsLib.GlobalWorkerOptions.workerSrc = './pdf.worker.min.js';
export const FileUpload = ({ fileType, s3KeyPrefix = 'images', s3Key, label, hideLabel = false, errors, additionalInfo, helpText, onUploaded, disabled, buttonSize, buttonLabel, useDragAndDrop, maxFileSizeInMB = 10, // 10MB
tag, }) => {
    const { t } = useLocale();
    const inputId = useMemo(() => uniqueId('file-upload'), []);
    const [error, setError] = useState('');
    const fileRef = useRef(null);
    const errorList = compact((errors ? (Array.isArray(errors) ? errors : [errors]) : []).concat(error));
    const { s3Url } = useGetS3Url({ s3Key });
    const { handleUpload, uploading } = useUpload({
        onUploaded,
        s3KeyPrefix,
    });
    const fileExtns = values(MIME_TYPES[fileType]).join(', ');
    const handleBeforeUpload = async (file) => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? '';
        let isValid = file.type in MIME_TYPES[fileType] ||
            values(MIME_TYPES[fileType])
                .map(x => x.toLowerCase())
                .includes(fileExtension);
        if (!isValid) {
            setError(t('X is not a valid file. Only Y are allowed', {
                values: { fileName: file.name, fileExtns },
            }));
        }
        else if (file.size / 1024 ** 2 > maxFileSizeInMB) {
            isValid = false;
            setError(t('File size should be less than {size} MB', { values: { size: maxFileSizeInMB } }));
        }
        if (fileExtension === 'pdf') {
            try {
                const arrayBuffer = await file.arrayBuffer();
                await pdfjsLib.getDocument({ data: arrayBuffer, password: '' }).promise;
                // If it loads, it's not locked
            }
            catch (e) {
                console.log('e', e);
                if (e.name === 'PasswordException') {
                    setError(t('Password-protected PDFs are not allowed'));
                    isValid = false;
                }
            }
        }
        return isValid;
    };
    const handleOnChange = async (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            await handleAdd(file);
        }
    };
    const handleAdd = async (file) => {
        if (!(await handleBeforeUpload(file)))
            return;
        setError('');
        await handleUpload({ file, onError: () => setError(t('Failed to upload file')) });
        if (fileRef.current)
            fileRef.current.value = ''; // clear the input
    };
    const handleClick = () => {
        if (fileRef.current) {
            fileRef.current.click();
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(InputLabelWrapper, { inputId: inputId, hideLabel: hideLabel, label: label, additionalInfo: additionalInfo, helpText: helpText }), (!s3Url && (_jsx(_Fragment, { children: useDragAndDrop ? (_jsxs("div", { className: styles.dndContainer, children: [uploading && (_jsx("div", { className: styles.spinner, children: _jsx(LoaderCircleIcon, {}) })), _jsx(FileUploader, { label: label, onAdd: handleAdd, disabled: disabled || uploading, dropZoneProps: {
                                accept: Object.keys(MIME_TYPES[fileType]).join(','),
                            }, supportedText: t('Supported formats: {extns}', {
                                values: {
                                    extns: fileExtns,
                                },
                            }), dropZoneText: _jsxs(_Fragment, { children: [t('Drag & Drop'), " ", t('or'), ' ', _jsx(TrackableButton, { btnType: 'link', enText: 'Upload a file', tag: tag || 'upload', children: t('Upload a file') })] }), maxFileSizeText: t('Max file size: {size} MB', {
                                values: { size: maxFileSizeInMB },
                            }), errors: errorList })] })) : (_jsx(TrackableButton, { enText: 'Upload', tag: tag || 'upload', appearance: buttonLabel === '' ? 'ghost' : undefined, disabled: disabled || uploading, onClick: handleClick, buttonSize: buttonSize, children: _jsxs("div", { className: 'flex flexItems--center', children: [buttonLabel ?? t('Upload Document'), _jsx(FileUploadOutlinedIcon, { className: 'margin--left_8', size: 24 })] }) })) }))) ||
                null, _jsx("input", { ref: fileRef, id: inputId, className: 'sr-only', type: 'file', onChange: handleOnChange, accept: Object.keys(MIME_TYPES[fileType]).join(',') }), !useDragAndDrop && (_jsx(ErrorList, { errors: errorList, show: errorList.length > 0, global: false }))] }));
};
